import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthService } from 'src/app/service/auth.service';
import { YukkApi } from 'src/app/service/yukkapi.service';
import { DateUtils } from 'src/app/shared/utils/date.utils';
import {
  EventsUtilsService,
  EventsTimeSeriesDataPoint,
} from 'src/app/shared/utils/events-utils.service';
import { QueryParams } from 'src/app/shared/models/query-params.interface';
import {
  ScoreDataPoint,
  VolumeDataPoint,
  EventsDataPoint,
  ScannerChartData,
  MergeChartDataInput,
} from './scanner-chart.models';
import { ScannerScoreStateService } from '../scanner-score-state.service';

@Injectable({
  providedIn: 'root',
})
export class ScannerChartDataService {
  /** Expose score loading state from shared service */
  readonly scoreLoading$ = this.scannerScoreState.loading$;

  /** Expose score state from shared service */
  readonly scoreState$ = this.scannerScoreState.scoreState$;

  /** Expose score error from shared service */
  readonly scoreError$ = this.scannerScoreState.error$;

  constructor(
    private auth: AuthService,
    private yukkApi: YukkApi,
    private eventsUtilsService: EventsUtilsService,
    private scannerScoreState: ScannerScoreStateService,
  ) {}

  fetchChartData(params: QueryParams): Observable<ScannerChartData> {
    const isSentiment = this.auth.scorelabSettings.defaultScore === 'sentiment';

    return forkJoin({
      scoreState: this.scannerScoreState.fetchScoreData(params),
      volume: this.fetchVolumeTimeSeries(params),
      events: this.fetchEventsTimeSeries(),
    }).pipe(
      map(({ scoreState, volume, events }) => {
        const hasEsgSubScores =
          scoreState.scoreEData.length > 0 ||
          scoreState.scoreSData.length > 0 ||
          scoreState.scoreGData.length > 0;

        const { sentimentShortLabel, sentimentLongLabel } =
          this.getSentimentLabels(params);

        return {
          ...this.mergeChartData({
            score: !isSentiment ? scoreState.scoreData : undefined,
            volume,
            events,
            scoreE: hasEsgSubScores ? scoreState.scoreEData : undefined,
            scoreS: hasEsgSubScores ? scoreState.scoreSData : undefined,
            scoreG: hasEsgSubScores ? scoreState.scoreGData : undefined,
            scoreSentiLong: isSentiment
              ? scoreState.scoreSentiLongData
              : undefined,
            scoreSentiShort: isSentiment
              ? scoreState.scoreSentiShortData
              : undefined,
          }),
          sentimentShortLabel,
          sentimentLongLabel,
        };
      }),
    );
  }

  private getSentimentLabels(params: QueryParams): {
    sentimentShortLabel: string;
    sentimentLongLabel: string;
  } {
    const timeframe = params.time || '7';
    const timeframeLength = DateUtils.chartFromTo(timeframe)?.period;
    const shortPeriod = timeframeLength <= 7 ? 1 : 7;
    const longPeriod = timeframeLength <= 7 ? 7 : 30;

    return {
      sentimentShortLabel: `${shortPeriod} Day Sentiment`,
      sentimentLongLabel: `${longPeriod} Day Sentiment`,
    };
  }

  /**
   * Fetches volume time series using legacy API
   */
  private fetchVolumeTimeSeries(
    params: QueryParams,
  ): Observable<VolumeDataPoint[]> {
    return this.yukkApi.scoresTimeSeries('volume', params, 'chart').pipe(
      map((response: { volume_ts?: VolumeDataPoint[] }) => {
        return response?.volume_ts || [];
      }),
      catchError(() => of([])),
    );
  }

  /**
   * Fetches events time series via POST /v5/api/events.
   */
  private fetchEventsTimeSeries(): Observable<EventsTimeSeriesDataPoint[]> {
    return this.eventsUtilsService.fetchEventsTimeSeries();
  }

  /**
   * Merges score, volume, and events data, aligning by date
   */
  private mergeChartData(input: MergeChartDataInput): ScannerChartData {
    const {
      score,
      volume,
      events,
      scoreE,
      scoreS,
      scoreG,
      scoreSentiLong,
      scoreSentiShort,
    } = input;

    const dates = this.resolvePrimaryDates(score, scoreSentiLong, volume);
    const volumeMap = new Map(volume.map((item) => [item.date, item.volume]));
    const eventsMap = new Map(events.map((item) => [item.date, item.count]));

    const alignedVolumeData: VolumeDataPoint[] = dates.map((date) => ({
      date,
      volume: volumeMap.get(date) || 0,
    }));

    const alignedEventsData: EventsDataPoint[] = dates.map((date) => ({
      date,
      count: eventsMap.get(date) || 0,
    }));

    return {
      scoreData: score || [],
      volumeData: alignedVolumeData,
      eventsData: alignedEventsData,
      dates,
      ...(scoreE && { scoreEData: scoreE }),
      ...(scoreS && { scoreSData: scoreS }),
      ...(scoreG && { scoreGData: scoreG }),
      ...(scoreSentiLong && { scoreSentiLongData: scoreSentiLong }),
      ...(scoreSentiShort && { scoreSentiShortData: scoreSentiShort }),
    };
  }

  /**
   * Determines the primary date array to use for alignment
   */
  private resolvePrimaryDates(
    score?: ScoreDataPoint[],
    scoreSentiLong?: ScoreDataPoint[],
    volume?: VolumeDataPoint[],
  ): string[] {
    if (score?.length) {
      return score.map((item) => item.date);
    }
    if (scoreSentiLong?.length) {
      return scoreSentiLong.map((item) => item.date);
    }
    return volume?.map((item) => item.date) || [];
  }
}
